/**
 * A start date is a calendar day, not an instant. A datepicker hands out local midnight, so
 * `toISOString()` on it moves the day backwards for every timezone east of UTC — picking the 14th in
 * Amsterdam sends `2026-08-13T22:00:00Z`, and the backend dates the subscription on the 13th. These
 * helpers keep the day the buyer sees by carrying it as `YYYY-MM-DD` and only widening it to a
 * date-time at the API boundary.
 */

/** The local calendar day of a Date, as `YYYY-MM-DD`. */
export function formatDateOnly(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * `YYYY-MM-DD` as a Date at local midnight — what a datepicker expects. Plain `new Date('2026-08-14')`
 * would read it as UTC and land on the 13th west of UTC.
 */
export function parseDateOnly(value: string): Date {
  const [year, month, day] = value.slice(0, 10).split('-').map(Number);
  return new Date(year, (month ?? 1) - 1, day ?? 1);
}

/** Today's calendar day at local midnight — the day itself, with no time of day on it. */
export function startOfToday(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

/**
 * The calendar day out of whatever the API returned (`2026-08-14`, `2026-08-14T00:00:00Z`, …), read
 * off the string rather than through a Date so no offset can shift it.
 */
export function dateOnlyFromApi(value: string): string | null {
  const day = value?.slice(0, 10) ?? '';
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : null;
}

/** A calendar day as the date-time the API takes: midnight UTC, so its UTC date is that day. */
export function dateOnlyToApi(value: string): string {
  return `${value}T00:00:00.000Z`;
}

/**
 * The last calendar day a billing period covers (`YYYY-MM-DD`), read off the API's **exclusive**
 * `period_end` — the first instant not covered. A period ending `2026-10-01` is paid through
 * 30 September, so printing the raw value names a day the buyer is not paying for (PAUL-3292).
 */
export function lastCoveredDayFromApi(periodEnd: string | null | undefined): string | null {
  const day = periodEnd ? dateOnlyFromApi(periodEnd) : null;
  if (!day) {
    return null;
  }
  const date = parseDateOnly(day);
  date.setDate(date.getDate() - 1);
  return formatDateOnly(date);
}

/** A calendar day (`YYYY-MM-DD`) as a billing window prints it: `26.08`, day first. */
export function formatDayMonth(dateOnly: string): string {
  const [, month, day] = dateOnly.slice(0, 10).split('-');
  return `${day}.${month}`;
}
